import { CREATE_FEE, formatMoney, GAME_DEFINITIONS, RESIZE_FEE, SERVER_SIZES, SIZE_PRICE_PER_HOUR, SIZE_SPECS } from '@hearth/shared';
import { Shell } from '../app/Shell';
import { linkTo } from '../router';
import { CONTACT_EMAIL, LEGAL_UPDATED, OPERATOR } from './SiteFooter';

// /terms: the Terms of Service as approved on the canvas (2026-10-27). Prices come from billing.ts,
// so the table never disagrees with the bill; raising one still needs 30 days' notice (section 3).

const USAGE_GUIDELINES = 'https://www.minecraft.net/en-us/usage-guidelines';

export function Terms() {
  const email = <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>;
  const hourly = SERVER_SIZES.map((size) => `${SIZE_SPECS[size].name} ${formatMoney(SIZE_PRICE_PER_HOUR[size])}`).join(', ');
  return (
    <Shell home="/">
      <article className="legal">
        <h1>Terms of Service</h1>
        <p className="quiet">{`Last updated ${LEGAL_UPDATED}`}</p>

        <p>These terms are the agreement between you and {OPERATOR}, who runs Hearth ("we"). By signing in or using Hearth you agree to them and to our <a {...linkTo('/privacy')}>Privacy Policy</a>. If you don't agree, please don't use Hearth.</p>

        <h2>1. Who can use Hearth</h2>
        <ul>
          <li>You must be 13 or older. If you're under 18, a parent or guardian must agree to these terms for you.</li>
          <li>To own a server or add a payment method you must be 18 or older.</li>
          <li>You sign in with your own Google or Discord account and are responsible for what happens under it. One person per account.</li>
        </ul>

        <h2>2. What Hearth does</h2>
        <p>Hearth runs game servers that you and the friends you invite can play on. Servers fall asleep when nobody's playing and wake when you ask. A server that has slept for a while may take longer than usual to wake. We take backups when a server falls asleep and keep the newest 10.</p>
        <p>We work hard to keep Hearth running, but we can't promise it will always be available or that a backup will always be there. Keep your own copy of anything you can't lose.</p>

        <h2>3. Prices and payment</h2>
        <div className="table-box">
          <table>
            <thead><tr><th>Charge</th><th>Price</th></tr></thead>
            <tbody>
              <tr><td>Creating a server</td><td>{`${formatMoney(CREATE_FEE)}, once`}</td></tr>
              <tr><td>Each hour a server is awake</td><td>{hourly}</td></tr>
              <tr><td>Changing a server's size</td><td>{`${formatMoney(RESIZE_FEE)} each time`}</td></tr>
              <tr><td>A server that's asleep</td><td>Free</td></tr>
            </tbody>
          </table>
        </div>
        <ul>
          <li>Charges go to the server's owner, whoever woke or changed it. Awake time is counted to the second.</li>
          <li>On the 1st of each month we charge the payment method you saved for the month before. Prices are in US dollars, plus any tax that applies.</li>
          <li>If a payment fails, you can't create servers until it's paid, and we may put your servers to sleep after 14 days unpaid.</li>
          <li>We'll give at least 30 days' notice of a price increase. Time already used is billed at the price it ran at.</li>
          <li><strong>Refunds:</strong> charges for time a server was awake and fees for actions you took aren't refundable. If we charged you by mistake, or Hearth failed and your server couldn't run, tell us within 60 days and we'll make it right.</li>
          <li>To stop paying, destroy your servers. You'll get one last bill for what they used that month.</li>
        </ul>

        <h2>4. Using Hearth fairly</h2>
        <p>Don't use Hearth to:</p>
        <ul>
          <li>break the law, or share content that's illegal, or that you don't have the right to share;</li>
          <li>harass, threaten or exploit anyone, especially children;</li>
          <li>attack, scan or overload other computers, send spam, or mine cryptocurrency;</li>
          <li>get around Hearth's limits, billing or security, or reach anyone else's servers or data;</li>
          <li>break the <a href={GAME_DEFINITIONS['minecraft-java'].eula.url}>Minecraft End User License Agreement</a> and <a href={USAGE_GUIDELINES}>Usage Guidelines</a>, which you accept when you create a server.</li>
        </ul>
        <p>Server owners are responsible for what happens on their servers and who they invite.</p>

        <h2>5. Your worlds and content</h2>
        <p>Your worlds and anything you upload stay yours. You give us permission to store, copy, run and back them up only as needed to provide Hearth to you. You confirm you have the right to upload what you upload.</p>
        <p><strong>Copyright:</strong> if you think something on Hearth infringes your copyright, send a notice to our designated agent, {OPERATOR}, at {email}. We remove infringing content and close the accounts of repeat infringers.</p>

        <h2>6. Ending things</h2>
        <p>You can stop using Hearth at any time and ask us to delete your account. We may suspend or close an account that breaks these terms or leaves bills unpaid, and will tell you why unless the law or safety prevents it. If we ever shut Hearth down, we'll give you at least 30 days to download your worlds.</p>

        <h2>7. Not affiliated with Minecraft</h2>
        <p>Not an official Minecraft product. Not approved by or associated with Mojang or Microsoft.</p>

        <h2>8. Disclaimers</h2>
        <p className="caps">Hearth is provided "as is" and "as available". To the extent the law allows, we disclaim all warranties, express or implied, including merchantability, fitness for a particular purpose and non-infringement.</p>

        <h2>9. Limits on liability</h2>
        <p className="caps">To the extent the law allows, we are not liable for indirect, incidental, special, consequential or punitive damages, or for lost data, profits or goodwill. Our total liability for any claim about Hearth is limited to the greater of what you paid us in the 12 months before the claim, or $50.</p>
        <p>Some places don't allow these limits, so they may not all apply to you. Nothing here limits rights you have as a consumer that the law says can't be limited.</p>

        <h2>10. If something goes wrong between us</h2>
        <p>Please contact us first: most problems are fixed with a message. You agree to cover our reasonable costs if a claim against us arises from your breaking these terms. These terms are governed by the laws of the State of Washington, USA, and disputes go to the state or federal courts for Pierce County, Washington, unless the law where you live gives you the right to bring them there.</p>

        <h2>11. Changes</h2>
        <p>We may update these terms. We'll change the date above and email you at least 30 days before a change that matters; continuing to use Hearth after that means you accept it.</p>

        <h2>12. Contact</h2>
        <p>The <a {...linkTo('/support')}>support form</a>, or {email}.</p>
      </article>
    </Shell>
  );
}
